import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#4A624E] transition-colors";

const ContactForm = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: t.contact.subjects[0], message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: t.contact.subjects[0], message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — veuillez vérifier vos informations.");
    } finally {
      setSending(false);
    }
  };
  return (
    <motion.form
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.15 }}
      onSubmit={submit}
      className="space-y-4 bg-white rounded-2xl border border-stone-200 p-6 lg:p-8"
      data-testid="contact-form"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="contact-name-input" required minLength={2} maxLength={80} className={inputCls} placeholder={t.contact.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="contact-email-input" required type="email" className={inputCls} placeholder={t.contact.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <select data-testid="contact-subject-select" className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
        {t.contact.subjects.map((s) => <option key={s}>{s}</option>)}
      </select>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} />
      <textarea data-testid="contact-message-input" required minLength={10} maxLength={3000} rows={5} className={inputCls} placeholder={t.contact.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={sending}
        className="w-full rounded-full bg-stone-900 text-[#FAF7F2] py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#4A624E] transition-colors duration-300 disabled:opacity-60"
      >
        {sending ? "..." : t.contact.send}
      </button>
    </motion.form>
  );
};

const NewsletterSection = ({ t }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.already_subscribed ? t.newsletter.already : t.newsletter.success);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — email invalide.");
    } finally {
      setSending(false);
    }
  };
  return (
    <section className="bg-[#4A624E] py-20 lg:py-28 px-6" data-testid="newsletter-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl mx-auto text-center"
      >
        <h2 className="font-serif text-3xl lg:text-4xl tracking-tight text-[#EFEAE1]">{t.newsletter.title}</h2>
        <p className="mt-4 text-sm text-[#EFEAE1]/70 leading-relaxed max-w-md mx-auto">{t.newsletter.sub}</p>
        <form onSubmit={submit} className="mt-8 flex gap-2 max-w-md mx-auto">
          <input
            data-testid="newsletter-email-input"
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.newsletter.placeholder}
            className="flex-1 rounded-full bg-[#EFEAE1]/10 border border-[#EFEAE1]/30 px-5 py-3.5 text-sm text-[#EFEAE1] placeholder:text-[#EFEAE1]/40 focus:outline-none focus:border-[#EFEAE1] transition-colors"
          />
          <button
            data-testid="newsletter-submit-button"
            type="submit"
            disabled={sending}
            className="group rounded-full bg-[#EFEAE1] text-[#2D3F30] px-6 py-3.5 text-[12px] uppercase tracking-[0.15em] flex items-center gap-2 hover:bg-[#C85A32] hover:text-white transition-colors duration-300 disabled:opacity-60"
          >
            {t.newsletter.cta}
            <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </form>
      </motion.div>
    </section>
  );
};

export const ContactNewsletter = () => {
  const { t, lang } = useStore();

  return (
    <>
      <section id="contact" className="py-24 lg:py-36 px-6 lg:px-14 xl:px-24 max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-16" data-testid="contact-section">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C85A32]" />
            {t.contact.overline}
          </p>
          <h2 className="font-serif text-3xl lg:text-4xl tracking-tight text-stone-900 max-w-md">{t.contact.title}</h2>
          <div className="mt-10 space-y-3 text-sm text-stone-600">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">NŪRA</p>
            <p>{lang === "fr" ? "Boissons fonctionnelles sans sucre ajouté." : "Functional drinks with no added sugar."}</p>
            <p>{lang === "fr" ? "Nous répondons par email à chaque message reçu via ce formulaire." : "We reply by email to every message sent through this form."}</p>
          </div>
        </motion.div>

        <ContactForm t={t} />
      </section>

      <NewsletterSection t={t} />
    </>
  );
};
